<template>
  <div class="container-fluid py-4">
    <div class="row">
      <div class="col-lg-12 mb-4">
        <div class="card">
          <div class="card-header pb-0">
            <div class="row">
              <div class="col-lg-6 col-7">
                <h6>Recebimentos por Empresa</h6>
              </div>
              <div class="col-lg-6 col-5 my-auto text-end">
                <div class="float-end" style="width: 150px;">
                  <input-month 
                    :key="refreshKey"
                    v-model="mesAtual" 
                    text-align="right"
                    @update:model-value="onMonthSelected"
                  />
                </div>
              </div>
            </div>
          </div>
          <div class="card-body px-0 pb-2">
            <!-- Carregando -->
            <div v-if="isLoading" class="text-center py-4">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Carregando...</span>
              </div>
              <p class="mt-3">Carregando dados...</p>
            </div>
            <!-- Sem dados -->
            <div v-else-if="!recebimentos || recebimentos.length === 0" class="text-center py-4">
              <i class="fas fa-exclamation-circle fa-3x text-warning mb-3"></i>
              <p>Nenhum dado de recebimento encontrado para o período selecionado.</p>
            </div>
            <!-- Gráficos -->
            <div v-else class="row px-4">
              <div class="col-lg-6 mb-2">
                <div class="card">
                  <div class="card-header pb-0">
                    <h6>Distribuição por Tipo de Recebimento</h6>
                  </div>
                  <div class="card-body p-3">
                    <div class="chart">
                      <pie-chart :chart-data="chartDataPorMetodo" :height="250" />
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-lg-6 mb-2">
                <div class="card">
                  <div class="card-header pb-0">
                    <h6>Top 10 Empresas por Recebimento</h6>
                  </div>
                  <div class="card-body p-3">
                    <div class="chart">
                      <bar-chart :chart-data="chartDataEmpresas" :height="250" />
                    </div>
                  </div>
                </div>
              </div>

              <!-- Resumo -->
              <div class="col-12">
                <div class="card">
                  <div class="card-header pb-0">
                    <h6>Resumo dos Recebimentos</h6>
                  </div>
                  <div class="card-body p-3">
                    <div class="row">
                      <div class="col-lg-3 col-md-6 mb-2">
                        <div class="card card-background card-background-mask-primary">
                          <div class="full-background"></div>
                          <div class="card-body p-3 text-center">
                            <h5 class="text-white mb-0">Total Recebido</h5>
                            <h3 class="text-white">{{ formatCurrency(totalRecebido) }}</h3>
                          </div>
                        </div>
                      </div>
                      <div class="col-lg-3 col-md-6 mb-2">
                        <div class="card card-background card-background-mask-success">
                          <div class="full-background"></div>
                          <div class="card-body p-3 text-center">
                            <h5 class="text-white mb-0">Total Cartão</h5>
                            <h3 class="text-white">{{ formatCurrency(totalCartao) }}</h3>
                          </div>
                        </div>
                      </div>
                      <div class="col-lg-3 col-md-6 mb-2">
                        <div class="card card-background card-background-mask-info">
                          <div class="full-background"></div>
                          <div class="card-body p-3 text-center">
                            <h5 class="text-white mb-0">Total PIX</h5>
                            <h3 class="text-white">{{ formatCurrency(totalPix) }}</h3>
                          </div>
                        </div>
                      </div>
                      <div class="col-lg-3 col-md-6 mb-2">
                        <div class="card card-background card-background-mask-warning">
                          <div class="full-background"></div>
                          <div class="card-body p-3 text-center">
                            <h5 class="text-white mb-0">Total Empresas</h5>
                            <h3 class="text-white">{{ totalEmpresas }}</h3>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Tabela de dados -->
              <div class="col-12 mt-2">
                <div class="card mb-4">
                  <div class="card-header pb-0">
                    <div class="row">
                      <div class="col-6">
                        <h6>Detalhamento dos Recebimentos</h6>
                      </div>
                      <div class="col-6 text-end">
                        <div class="d-flex justify-content-end">
                          <button class="btn btn-sm btn-primary me-2" @click="exportarCSV">
                            <i class="fas fa-download me-1"></i> Exportar CSV
                          </button>
                          <div class="input-group" style="width: 250px;">
                            <span class="input-group-text">
                              <i class="fas fa-search"></i>
                            </span>
                            <input class="form-control" placeholder="Buscar empresa..." type="text" v-model="searchQuery">
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="card-body px-0 pt-0 pb-2">
                    <div class="table-responsive p-0" style="max-height: 500px; overflow-y: auto;">
                      <table class="table align-items-center mb-0" id="tabelaRecebimentos">
                        <thead>
                          <tr>
                            <th @click="sortBy('nome_fantasia')"
                              class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2 cursor-pointer">
                              Empresa
                              <i v-if="sortColumn === 'nome_fantasia'"
                                :class="sortDirection === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down'"></i>
                            </th>
                            <th @click="sortBy('credito')"
                              class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2 cursor-pointer">
                              Crédito
                              <i v-if="sortColumn === 'credito'"
                                :class="sortDirection === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down'"></i>
                            </th>
                            <th @click="sortBy('debito')"
                              class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2 cursor-pointer">
                              Débito
                              <i v-if="sortColumn === 'debito'"
                                :class="sortDirection === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down'"></i>
                            </th>
                            <th @click="sortBy('total_cartao')"
                              class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2 cursor-pointer">
                              Total Cartão
                              <i v-if="sortColumn === 'total_cartao'"
                                :class="sortDirection === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down'"></i>
                            </th>
                            <th @click="sortBy('total_pix')"
                              class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2 cursor-pointer">
                              Total PIX
                              <i v-if="sortColumn === 'total_pix'"
                                :class="sortDirection === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down'"></i>
                            </th>
                            <th @click="sortBy('total_recebido')"
                              class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2 cursor-pointer">
                              Total
                              <i v-if="sortColumn === 'total_recebido'"
                                :class="sortDirection === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down'"></i>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(item, index) in filteredRecebimentos" :key="index">
                            <td>
                              <div class="d-flex px-2 py-1">
                                <div class="d-flex flex-column justify-content-center">
                                  <h6 class="mb-0 text-sm">{{ item.nome_fantasia }}</h6>
                                  <p class="text-xs text-secondary mb-0">{{ item.razao_social }}</p>
                                </div>
                              </div>
                            </td>
                            <td>
                              <p class="text-xs font-weight-bold mb-0">{{ formatCurrency(parseFloat(item.credito)) }}
                              </p>
                            </td>
                            <td>
                              <p class="text-xs font-weight-bold mb-0">{{ formatCurrency(parseFloat(item.debito)) }}</p>
                            </td>
                            <td>
                              <p class="text-xs font-weight-bold mb-0">{{ formatCurrency(parseFloat(item.total_cartao))
                                }}</p>
                            </td>
                            <td>
                              <p class="text-xs font-weight-bold mb-0">{{ formatCurrency(parseFloat(item.total_pix)) }}
                              </p>
                            </td>
                            <td class="font-weight-bold">
                              <p class="text-xs font-weight-bold mb-0">{{
                                formatCurrency(parseFloat(item.total_recebido)) }}</p>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';
import { errorToast } from '@/utils/alerts';
import { BarChart, PieChart } from '@/components/Charts';
import InputMonth from '@/components/InputMonth.vue';
import moment from 'moment';
import { exportToCSV } from '@/utils/export';

export default {
  name: "DashboardRecebimentosEmpresas",
  components: {
    PieChart,
    BarChart,
    InputMonth
  },
  data() {
    return {
      isLoading: false,
      mesAtual: moment().startOf('month').toDate(),
      recebimentos: [],
      searchQuery: '',
      sortColumn: 'total_recebido',
      sortDirection: 'desc',
      refreshKey: 0,
      chartColors: [
        '#2152ff', '#3A416F', '#f53939', '#fb6340', '#17c1e8',
        '#82d616', '#ea0606', '#f5b00c', '#627594', '#8392ab'
      ]
    };
  },
  computed: {
    mesAtualFormatado() {
      return moment(this.mesAtual).format('MMMM/YYYY');
    },
    filteredRecebimentos() {
      let filtered = [...this.recebimentos];

      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(item =>
          (item.nome_fantasia && item.nome_fantasia.toLowerCase().includes(query)) ||
          (item.razao_social && item.razao_social.toLowerCase().includes(query))
        );
      }

      // Ordenação
      filtered.sort((a, b) => {
        let aValue = a[this.sortColumn];
        let bValue = b[this.sortColumn];

        // Converter para números se forem strings de números
        if (typeof aValue === 'string' && !isNaN(parseFloat(aValue))) {
          aValue = parseFloat(aValue);
        }
        if (typeof bValue === 'string' && !isNaN(parseFloat(bValue))) {
          bValue = parseFloat(bValue);
        }

        if (typeof aValue === 'number') {
          return this.sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
        } else {
          if (this.sortDirection === 'asc') {
            return String(aValue).localeCompare(String(bValue));
          } else {
            return String(bValue).localeCompare(String(aValue));
          }
        }
      });

      return filtered;
    },
    totalRecebido() {
      return this.recebimentos.reduce((total, item) => {
        const value = parseFloat(item.total_recebido || 0);
        return !isNaN(value) ? total + value : total;
      }, 0);
    },
    totalCartao() {
      return this.recebimentos.reduce((total, item) => {
        const value = parseFloat(item.total_cartao || 0);
        return !isNaN(value) ? total + value : total;
      }, 0);
    },
    totalPix() {
      return this.recebimentos.reduce((total, item) => {
        const value = parseFloat(item.total_pix || 0);
        return !isNaN(value) ? total + value : total;
      }, 0);
    },
    totalEmpresas() {
      return this.recebimentos.length;
    },
    chartDataPorMetodo() {
      // Calculate totals for each payment type
      const totalCredito = this.recebimentos.reduce((total, item) => total + parseFloat(item.credito || 0), 0);
      const totalDebito = this.recebimentos.reduce((total, item) => total + parseFloat(item.debito || 0), 0);
      const totalPix = this.recebimentos.reduce((total, item) => total + parseFloat(item.total_pix || 0), 0);

      // Filter out zero values
      const labels = [];
      const data = [];
      const colors = [];

      if (totalCredito > 0) {
        labels.push(`Cartão de Crédito: ${this.formatCurrency(totalCredito)}`);
        data.push(totalCredito);
        colors.push(this.chartColors[0]); // Azul para Crédito
      }

      if (totalDebito > 0) {
        labels.push(`Cartão de Débito: ${this.formatCurrency(totalDebito)}`);
        data.push(totalDebito);
        colors.push(this.chartColors[1]); // Cinza para Débito
      }

      if (totalPix > 0) {
        labels.push(`PIX: ${this.formatCurrency(totalPix)}`);
        data.push(totalPix);
        colors.push(this.chartColors[4]); // Azul claro para PIX
      }

      return {
        labels: labels,
        datasets: [{
          label: 'Valor Recebido',
          data: data,
          backgroundColor: colors,
          borderWidth: 0
        }]
      };
    },
    chartDataEmpresas() {
      // Ordenar empresas por total recebido e pegar as top 10
      const empresas = [...this.recebimentos]
        .sort((a, b) => parseFloat(b.total_recebido) - parseFloat(a.total_recebido))
        .slice(0, 10);

      return {
        labels: empresas.map(e => e.nome_fantasia),
        datasets: [{
          label: 'Total Recebido',
          data: empresas.map(e => parseFloat(e.total_recebido)),
          backgroundColor: this.chartColors[1],
          borderColor: this.chartColors[1],
          borderWidth: 1
        }]
      };
    }
  },
  mounted() {
    // Configurar moment.js para português
    moment.locale('pt-br');

    this.fetchData();
  },
  methods: {
    async fetchData() {
      this.isLoading = true;
      try {
        const mesFormatado = moment(this.mesAtual).format('YYYY-MM');
        const response = await api.get(`/recebimentos-empresas/${mesFormatado}`);
        this.recebimentos = response.data;
      } catch (error) {
        errorToast('Erro ao buscar dados de recebimentos');
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },
    formatCurrency(value) {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(value);
    },
    sortBy(column) {
      if (this.sortColumn === column) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortColumn = column;
        this.sortDirection = 'desc'; // Começa ordenando pelo maior valor
      }
    },
    onMonthSelected(newDate) {
      this.mesAtual = newDate;
      this.refreshKey++; // Force refresh the component
      this.fetchData();
    },
    exportarCSV() {
      const nomeMes = moment(this.mesAtual).format('MMMM-YYYY');
      exportToCSV('tabelaRecebimentos', `recebimentos-empresas-${nomeMes}.csv`);
    }
  }
};
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>